
import React, { useState } from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

const FAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  const faqs = [
    {
      question: "Quanto tempo dura uma sessão fotográfica em Balneário Camboriú?",
      answer: "O tempo de duração varia conforme o tamanho e tipo do imóvel. Em média, uma sessão para apartamento dura de 2 a 3 horas, enquanto casas maiores podem levar de 4 a 6 horas. Incluímos tempo para preparação do ambiente e múltiplos ângulos de cada cômodo. Atendemos toda região: Balneário Camboriú, Itajaí, Itapema e Porto Belo."
    },
    {
      question: "Quantas fotos são entregues no pacote de fotografia imobiliária?",
      answer: "O número de fotos varia conforme o pacote e o tamanho do imóvel, mas garantimos uma cobertura completa de todos os ambientes e arquitetura. Em média, entregamos entre 25 a 40 fotos em alta resolução por sessão, todas editadas profissionalmente para destacar o potencial de venda do imóvel."
    },
    {
      question: "Em quanto tempo recebo as fotos editadas do meu imóvel?",
      answer: "Nosso prazo padrão de entrega é de 48 horas úteis após a sessão fotográfica. Para projetos urgentes em Balneário Camboriú e região, oferecemos serviço express com entrega em 24 horas (consulte disponibilidade e taxas adicionais). Todas as fotos passam por edição profissional."
    },
    {
      question: "Vocês atendem Itajaí, Itapema e Porto Belo além de Balneário Camboriú?",
      answer: "Sim! Atendemos toda a região: Balneário Camboriú, Itajaí, Itapema, Porto Belo e cidades vizinhas. Para localidades mais distantes, consulte disponibilidade e possíveis taxas de deslocamento. Também realizamos projetos especiais de arquitetura e vídeos imobiliários em outras localidades."
    },
    {
      question: "É necessário preparar o imóvel antes da sessão de fotos?",
      answer: "Sim, a preparação é fundamental para obter os melhores resultados nas fotos. Enviamos um guia completo de preparação que inclui limpeza, organização, iluminação adequada e pequenos ajustes de decoração. Nossa equipe também oferece orientações no local para destacar a arquitetura e pontos fortes do imóvel."
    },
    {
      question: "Fazem filmagem com drone para vídeos imobiliários?",
      answer: "Sim! Oferecemos serviços completos de vídeos imobiliários com drone, ideais para mostrar a localização, área externa, arquitetura e contexto do imóvel em Balneário Camboriú e região. Seguimos todas as regulamentações da ANAC e possuímos certificações necessárias para voos comerciais."
    },
    {
      question: "Como funciona o tour virtual 360° para imóveis?",
      answer: "Utilizamos câmeras especializadas para capturar imagens 360° de cada ambiente. O resultado é uma experiência interativa onde os visitantes podem 'caminhar' pelo imóvel virtualmente, visualizando cada detalhe da arquitetura como se estivessem presentes fisicamente. Ideal para vendas à distância."
    },
    {
      question: "O que inclui o serviço de CRM imobiliário TocToc?",
      answer: "Nosso CRM imobiliário é uma plataforma completa para gestão de leads, clientes, imóveis e vendas. Inclui: organização de portfólio, follow-up automatizado, relatórios de desempenho, integração com redes sociais e ferramentas específicas para corretores de Balneário Camboriú, Itajaí, Itapema e Porto Belo."
    },
    {
      question: "Como funciona a documentação fotográfica completa do imóvel?",
      answer: "A documentação fotográfica inclui registro completo do estado atual do imóvel, com todos os detalhes da arquitetura, instalações e acabamentos. Geramos relatório detalhado com fotos catalogadas, ideal para locação, compra/venda ou documentação de seguro."
    },
    {
      question: "Oferecem desconto para múltiplos imóveis ou imobiliárias?",
      answer: "Sim! Para imobiliárias e corretores com múltiplos projetos em Balneário Camboriú e região, oferecemos descontos progressivos e pacotes especiais. Também temos parcerias para CRM imobiliário e serviços continuados. Entre em contato para conhecer nossas condições comerciais personalizadas."
    }
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://site.toctocbr.com/faq#page",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quanto tempo dura uma sessão fotográfica?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Em média, uma sessão de fotografia imobiliária dura cerca de 50 minutos. Em ensaios mais complexos, como imóveis para Airbnb, hospedagem e arquitetura, o tempo pode ser maior, considerando iluminação, composição e ângulos adicionais."
        }
      },
      {
        "@type": "Question",
        "name": "Quantas fotos são entregues por imóvel?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O número varia conforme o tamanho e o propósito do imóvel. Em média, entregamos entre 25 e 40 fotos profissionais em alta resolução. As imagens passam por edição completa e são entregues em até 48 horas úteis."
        }
      },
      {
        "@type": "Question",
        "name": "O que é o book do imóvel?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O book do imóvel é um conjunto completo de imagens que representam cada ambiente com o máximo de qualidade e propósito comercial. Inclui no mínimo quatro fotos por ambiente (exceto banheiros) e entre 20 e 40 fotos no total, dependendo do tipo e do tamanho do imóvel."
        }
      },
      {
        "@type": "Question",
        "name": "É necessário preparar o imóvel antes da sessão?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, recomendamos preparar o ambiente para garantir o melhor resultado. Organizar móveis, retirar objetos pessoais e abrir janelas ajudam a valorizar a luz e o espaço. Também oferecemos filmagem com drone (para homens e mulheres) quando o imóvel e a área permitem, especialmente em coberturas e imóveis com vista panorâmica."
        }
      },
      {
        "@type": "Question",
        "name": "Vocês oferecem serviço de drone?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim. A Toc Toc oferece fotografia e filmagem aérea com drone para imóveis residenciais, comerciais, arquitetura e hospedagem, permitindo imagens panorâmicas e vídeos com maior apelo visual."
        }
      },
      {
        "@type": "Question",
        "name": "Vocês oferecem tour virtual 360°?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Atualmente não oferecemos tour virtual 360° como serviço padrão. Nosso foco é na fotografia e filmagem profissional com equipamentos de alta resolução, incluindo drones, e integração com o CRM imobiliário Hunter para gestão dos materiais e anúncios."
        }
      },
      {
        "@type": "Question",
        "name": "O que está incluso no serviço de CRM imobiliário Toc Toc?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O CRM utilizado pela Toc Toc é o Hunter CRM Imobiliário — uma plataforma completa de gestão de leads, imóveis e comunicação. Ele atua como um assessor inteligente para corretores e imobiliárias, automatizando tarefas, integrando fotos e vídeos e otimizando o processo de venda. Saiba mais em https://www.huntercrm.com.br"
        }
      },
      {
        "@type": "Question",
        "name": "Oferecem desconto para múltiplos imóveis ou imobiliárias?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim. Oferecemos pacotes personalizados para imobiliárias, corretores e administradoras que realizam ensaios em escala. Os descontos variam conforme a quantidade de imóveis e a frequência de serviços contratados."
        }
      },
      {
        "@type": "Question",
        "name": "Quem pode contratar os serviços da Toc Toc?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Atendemos corretores autônomos, imobiliárias, construtoras, proprietários e anfitriões de Airbnb e outras plataformas de hospedagem em Balneário Camboriú, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas, Navegantes e demais cidades da região."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>FAQ Toc Toc | Dúvidas sobre Fotografia Imobiliária, Vídeos e Airbnb</title>
        <meta
          name="description"
          content="Tire suas dúvidas sobre fotografia imobiliária, arquitetura, vídeos, filmmaker, drone e Airbnb. Saiba prazos, número de fotos, duração da sessão e como funciona o book do imóvel. Toc Toc Fotografia Imobiliária e Hunter CRM Imobiliário."
        />
        <link rel="canonical" href="https://site.toctocbr.com/faq" />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:url" content="https://site.toctocbr.com/faq" />
        <meta property="og:site_name" content="Toc Toc" />
        <meta property="og:title" content="FAQ Toc Toc | Fotografia Imobiliária e Hunter CRM" />
        <meta
          property="og:description"
          content="Perguntas frequentes sobre fotografia imobiliária, arquitetura, vídeos, filmmaker, drone, Airbnb e CRM Hunter Imobiliário."
        />
        <meta property="og:image" content="https://site.toctocbr.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FAQ Toc Toc — Fotografia Imobiliária e Hunter CRM" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ Toc Toc | Fotografia Imobiliária e Hunter CRM" />
        <meta
          name="twitter:description"
          content="Perguntas frequentes sobre fotografia imobiliária, arquitetura, vídeos, filmmaker, drone, Airbnb e CRM Hunter Imobiliário."
        />
        <meta name="twitter:image" content="https://site.toctocbr.com/og-image.jpg" />

        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Dúvidas <span className="text-[#FF5605]">Frequentes</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Tudo o que você precisa saber sobre fotografia imobiliária, vídeos e CRM
                  em Balneário Camboriú, Itajaí, Itapema e Porto Belo.
                  Encontre respostas para as perguntas mais comuns sobre nossos serviços.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="container max-w-4xl mx-auto px-4">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <button
                      className="w-full px-6 py-6 text-left focus:outline-none focus:ring-2 focus:ring-[#FF5605] focus:ring-inset"
                      onClick={() => toggleItem(index)}
                    >
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-[#323031] pr-4">
                          {faq.question}
                        </h3>
                        <div className="flex-shrink-0">
                          {openItems.includes(index) ? (
                            <ChevronUp className="h-5 w-5 text-[#FF5605]" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-[#FF5605]" />
                          )}
                        </div>
                      </div>
                    </button>
                    
                    <div className={`overflow-hidden transition-all duration-300 ${
                      openItems.includes(index) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-4"></div>
                        <p className="text-[#5B5A59] leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Still Have Questions Section */}
          <section className="py-16 bg-[#F6E5C9]/20">
            <div className="container max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-6">
                Ainda tem <span className="text-[#FF5605]">dúvidas</span>?
              </h2>
              <p className="text-xl text-[#5B5A59] mb-8 max-w-2xl mx-auto">
                Nossa equipe está pronta para esclarecer todas as suas questões sobre
                fotografia imobiliária, vídeos e CRM em Balneário Camboriú e região.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-3 rounded-xl">
                  Falar no WhatsApp (47) 99736-7396
                </Button>
                <Button variant="outline" className="border-[#FF5605] text-[#FF5605] hover:bg-[#FF5605] hover:text-white px-8 py-3 rounded-xl">
                  Enviar E-mail
                </Button>
              </div>
            </div>
          </section>

          {/* Quick Contact */}
          <section className="py-12 bg-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <h4 className="font-semibold mb-2">Telefone/WhatsApp</h4>
                  <p className="text-white/80">(47) 99736-7396</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">E-mail</h4>
                  <p className="text-white/80">contato@toctocbr.com</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Cidades Atendidas</h4>
                  <p className="text-white/80">Balneário Camboriú, Itajaí, Itapema, Porto Belo</p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <TocTocFooter />
      </div>
    </>
  );
};

export default FAQ;
